<template>
    <div class="admin-container">
          <div class="admin-header">
      <h2>Admin Dashboard</h2>
      <div class="admin-nav">
        <router-link to="/admin_comics" class="nav-button">🎭 Comics</router-link>
        <router-link to="/admin_dates" class="nav-button">📊 Events</router-link>
        <router-link to="/admin_rota" class="nav-button">📋 Rota</router-link>
        <router-link
          v-if="isClubsAdmin"
          to="/admin_clubs"
          class="nav-button"
        >🏢 Clubs</router-link>
        <button class="logout-button" @click="logout">🚪 Logout</button>
      </div>
    </div>

      <section>
        <h3>Email Templates</h3>
        <textarea v-model="emailTemplate" class="large-textarea" placeholder="Edit email template here..."></textarea>
        <button class="primary-button" @click="saveTemplate">Save Template</button>
      </section>

      <section>
        <h3>Add Event Date</h3>
        <div class="input-group">
          <input
            type="date"
            v-model="eventDate"
            class="input-field"
            placeholder="Event Date"
          />
          <input
            type="text"
            v-model="eventName"
            class="input-field"
            placeholder="Event Name"
          />
          <button class="primary-button" @click="addEvent">Add Event</button>
        </div>
      </section>

      <section>
        <h3>Recurring Events Setup</h3>
        <div class="input-group">
          <div class="recurring-config">
            <label>
              Generate events until:
              <input
                type="date"
                v-model="recurringEndDate"
                class="input-field"
                :min="todayFormatted"
              />
            </label>
          </div>
          <button class="primary-button" @click="generateRecurringEvents">Generate Tuesday Events</button>
        </div>

        <div class="blackout-dates">
          <h4>Add Blackout Date</h4>
          <div class="input-group">
            <input
              type="date"
              v-model="blackoutDate"
              class="input-field"
              placeholder="Blackout Date"
            />
            <input
              type="text"
              v-model="blackoutReason"
              class="input-field"
              placeholder="Reason (e.g. Christmas)"
            />
            <button class="primary-button" @click="addBlackoutDate">Add Blackout</button>
          </div>

          <ul class="blackout-list">
            <li v-for="date in blackoutDates" :key="date.date">
              {{ new Date(date.date).toLocaleDateString() }} - {{ date.reason }}
              <button @click="removeBlackoutDate(date.date)" class="remove-button">×</button>
            </li>
          </ul>
        </div>
      </section>
    </div>
  </template>

  <script>
  import { supabase } from '../supabaseClient';
import Swal from 'sweetalert2';
import { logoutAdmin } from '../utils/auth.js';
import { getCurrentUser } from '../utils/supabaseAuth.js';
import { emailIsClubsAdmin } from '../utils/clubsAdminAccess.js';

  export default {
    name: 'AdminPage',
    data() {
      return {
        isClubsAdmin: false,
        emailTemplate: '',
        eventDate: '',
        eventName: '',
        recurringEndDate: '',
        blackoutDate: '',
        blackoutReason: '',
        blackoutDates: [],
        todayFormatted: new Date().toISOString().split('T')[0],
      };
    },
    methods: {
      async saveTemplate() {
        try {
          const { error } = await supabase
            .from('email_templates')
            .update({ body: this.emailTemplate.replace(/\n/g, '\\n') })
            .eq('id', 1); // Assuming you're editing the first template

          if (error) {
            console.error('Error saving template:', error);
            Swal.fire('Error', 'Failed to save the template. Please try again.', 'error');
          } else {
            Swal.fire('Success', 'Email template saved successfully!', 'success');
          }
        } catch (err) {
          console.error('Unexpected error:', err);
          Swal.fire('Error', 'An unexpected error occurred. Please try again.', 'error');
        }
      },
      async addEvent() {
        if (!this.eventDate || !this.eventName) {
          Swal.fire('Error', 'Please provide both the event date and name.', 'error');
          return;
        }

        try {
          const { error } = await supabase
            .from('events')
            .insert([{ event_date: this.eventDate, event_name: this.eventName }]);

          if (error) {
            console.error('Error adding event:', error);
            Swal.fire('Error', 'Failed to add the event. Please try again.', 'error');
          } else {
            Swal.fire('Success', 'Event added successfully!', 'success');
          }
        } catch (err) {
          console.error('Unexpected error:', err);
          Swal.fire('Error', 'An unexpected error occurred. Please try again.', 'error');
        }
      },
      async generateRecurringEvents() {
        if (!this.recurringEndDate) {
          Swal.fire('Error', 'Please select an end date', 'error');
          return;
        }

        const endDate = new Date(this.recurringEndDate);
        // const today = new Date();
        let currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        const events = [];
        while (currentDate <= endDate) {
          if (currentDate.getDay() === 2) { // Tuesday
            const isBlackout = this.blackoutDates.some(bd =>
              new Date(bd.date).toDateString() === currentDate.toDateString()
            );

            if (!isBlackout) {
              events.push({
                event_date: currentDate.toISOString().split('T')[0],
                event_name: 'Tuesday Night Comedy'
              });
            }
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }

        try {
          const { error } = await supabase
            .from('events')
            .insert(events);

          if (error) {
            console.error('Error adding events:', error);
            Swal.fire('Error', 'Failed to generate events. Please try again.', 'error');
          } else {
            Swal.fire('Success', `Generated ${events.length} events successfully!`, 'success');
          }
        } catch (err) {
          console.error('Unexpected error:', err);
          Swal.fire('Error', 'An unexpected error occurred. Please try again.', 'error');
        }
      },
      async addBlackoutDate() {
        if (!this.blackoutDate || !this.blackoutReason) {
          Swal.fire('Error', 'Please provide both date and reason', 'error');
          return;
        }

        try {
          const { error } = await supabase
            .from('blackout_dates')
            .insert({
              date: this.blackoutDate,
              reason: this.blackoutReason
            });

          if (error) {
            console.error('Error adding blackout date:', error);
            Swal.fire('Error', 'Failed to add blackout date', 'error');
            return;
          }

          // Update local state
          this.blackoutDates.push({
            date: this.blackoutDate,
            reason: this.blackoutReason
          });

          this.blackoutDate = '';
          this.blackoutReason = '';

          Swal.fire('Success', 'Blackout date added successfully', 'success');
        } catch (err) {
          console.error('Unexpected error:', err);
          Swal.fire('Error', 'An unexpected error occurred', 'error');
        }
      },
      async removeBlackoutDate(date) {
        try {
          const { error } = await supabase
            .from('blackout_dates')
            .delete()
            .eq('date', date);

          if (error) {
            console.error('Error removing blackout date:', error);
            Swal.fire('Error', 'Failed to remove blackout date', 'error');
            return;
          }

          this.blackoutDates = this.blackoutDates.filter(bd => bd.date !== date);
        } catch (err) {
          console.error('Unexpected error:', err);
          Swal.fire('Error', 'An unexpected error occurred', 'error');
        }
      },
      logout() {
        logoutAdmin();
        this.$router.push('/');
      }
    },
    async created() {
      const user = await getCurrentUser();
      this.isClubsAdmin = emailIsClubsAdmin(user?.email);

      try {
        const { data, error } = await supabase
            .from('email_templates')
            .select('body') // Assuming "body" is the column name
            .eq('id', 1);

        if (error) {
          console.error('Error fetching email template:', error);
        } else if (data && data.length > 0) {
          this.emailTemplate = data[0].body.replace(/\\n/g, '\n');
        }
      } catch (err) {
        console.error('Unexpected error:', err);
      }

      // Load blackout dates
      const { data: blackoutDates, error } = await supabase
        .from('blackout_dates')
        .select('*')
        .order('date', { ascending: true });

      if (error) {
        console.error('Error loading blackout dates:', error);
      } else {
        this.blackoutDates = blackoutDates;
      }
    }
  };
  </script>

  <style scoped>
  .admin-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px;
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    font-family: Arial, sans-serif;
  }

  .admin-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  h2 {
    color: #2c3e50;
    margin: 0;
  }

  .logout-button {
    background-color: #e74c3c;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.3s;
  }

  .logout-button:hover {
    background-color: #c0392b;
  }

  .admin-nav {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .nav-button {
    padding: 8px 16px;
    background: #6c757d;
    color: white;
    text-decoration: none;
    border-radius: 4px;
    font-size: 14px;
    transition: background-color 0.2s;
  }

  .nav-button:hover {
    background: #5a6268;
  }

  .nav-button.router-link-active {
    background: #007bff;
  }

  h3 {
    margin-top: 20px;
    color: #34495e;
  }

  .large-textarea {
    width: 100%;
    height: 550px;
    padding: 10px;
    margin-bottom: 15px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 16px;
  }

  .input-group {
    display: flex;
    gap: 10px;
    margin-top: 10px;
  }

  .input-field {
    flex: 1;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 16px;
  }

  .primary-button {
    background-color: #3498db;
    color: white;
    border: none;
    padding: 10px 20px;
    font-size: 16px;
    cursor: pointer;
    border-radius: 5px;
    transition: background-color 0.3s;
  }

  .primary-button:hover {
    background-color: #2980b9;
  }

  .blackout-list {
    list-style: none;
    padding: 0;
    margin-top: 10px;
  }

  .blackout-list li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px;
    margin: 5px 0;
    background: #f5f5f5;
    border-radius: 4px;
  }

  .remove-button {
    background: #e74c3c;
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .recurring-config {
    margin-bottom: 15px;
  }
  </style>
